import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useGrowthGoals2Y3X } from "@/hooks/useGrowthGoals2Y3X";
import { PriorityGoalSelector } from "@/components/scale-2y3x/PriorityGoalSelector";
import { AREA_OPTIONS, PERSPECTIVE_COLORS, getAreaDisplayLabel } from "@/components/scale-2y3x/constants";
import { useEditIdeaDialog } from "@/contexts/EditIdeaDialogContext";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useBrandManager } from "@/hooks/useBrandManager";
import { Flag } from "lucide-react";

type IdeaStatus = 'Draft' | 'Pending' | 'Approved' | 'Completed' | 'Split' | 'Dropped' | 'TBD';

const IDEA_STATUS_OPTIONS: { value: IdeaStatus; label: string }[] = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Split', label: 'Split' },
  { value: 'Dropped', label: 'Dropped' },
  { value: 'TBD', label: 'TBD' },
];

export const GlobalEditIdeaDialog: React.FC = () => {
  const location = useLocation();
  const { isOpen, idea, closeEditIdeaDialog, onSuccess } = useEditIdeaDialog();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [area, setArea] = useState<string>("");
  const [targetYear, setTargetYear] = useState<number>(1);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [flaggedToBeRevisited, setFlaggedToBeRevisited] = useState(false);
  const [taskStatus, setTaskStatus] = useState<IdeaStatus>('Draft');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { goals, isLoading: goalsLoading } = useGrowthGoals2Y3X();
  const { currentBrandId } = useBrandManager();

  // Fetch brand currency
  const { data: brandData } = useQuery({
    queryKey: ['brand-currency', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return null;
      
      const { data, error } = await supabase
        .from('brand_data')
        .select('currency')
        .eq('brand_id', currentBrandId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!currentBrandId,
  });

  // Get currency symbol
  const CURRENCY_SYMBOLS: { [key: string]: string } = {
    'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$',
    'CHF': 'CHF', 'CNY': '¥', 'SEK': 'kr', 'NZD': 'NZ$', 'MXN': '$', 'SGD': 'S$',
    'HKD': 'HK$', 'NOK': 'kr', 'ZAR': 'R', 'TRY': '₺', 'BRL': 'R$', 'INR': '₹',
    'KRW': '₩', 'PLN': 'zł', 'DKK': 'kr', 'CZK': 'Kč', 'HUF': 'Ft', 'ILS': '₪',
    'CLP': '$', 'PHP': '₱', 'AED': 'د.إ', 'COP': '$', 'SAR': '﷼', 'MYR': 'RM',
    'RON': 'lei', 'THB': '฿', 'BGN': 'лв', 'HRK': 'kn', 'RUB': '₽', 'UAH': '₴'
  };
  const currency = brandData?.currency || 'USD';
  const currencySymbol = CURRENCY_SYMBOLS[currency] || '$';

  useEffect(() => {
    if (idea) {
      setName(idea.name);
      setDescription(idea.description || "");
      setArea(idea.area || "");
      setTargetYear(idea.target_year || 1);
      setSelectedGoals(idea.affecting_metric || []);
      setFlaggedToBeRevisited(idea.flagged_to_be_revisited || false);
      setTaskStatus((idea.task_status as IdeaStatus) || 'Draft');
    }
  }, [idea]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Please enter an initiative name",
        variant: "destructive",
      });
      return;
    }

    if (!idea) return;

    try {
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from('growth_ideas')
        .update({
          name: name.trim(),
          description: description.trim() || null,
          area: area || null,
          target_year: targetYear,
          affecting_metric: selectedGoals,
          flagged_to_be_revisited: flaggedToBeRevisited,
          task_status: taskStatus,
        })
        .eq('id', idea.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Initiative updated successfully",
      });

      if (onSuccess) {
        onSuccess();
      }
      closeEditIdeaDialog();
    } catch (error: any) {
      console.error('Error updating idea:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update initiative",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      closeEditIdeaDialog();
    }
  };

  const handleGoalsChange = (goalIds: string[]) => {
    setSelectedGoals(goalIds);
  };

  if (!idea) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="bg-muted/50 -mx-6 -mt-6 px-6 py-4 mb-4">
          <DialogTitleWithBug
            popupName="Edit 2Y3X Initiative"
            popupContext={location.pathname}
            isOpen={isOpen}
          >
            Edit Initiative
          </DialogTitleWithBug>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Status and Flag Row */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Status Dropdown */}
            <div className="flex-1 space-y-2">
              <Label htmlFor="global-edit-idea-status">Status</Label>
              <Select value={taskStatus} onValueChange={(value: IdeaStatus) => setTaskStatus(value)} disabled={isSubmitting}>
                <SelectTrigger id="global-edit-idea-status" className="bg-background">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {IDEA_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Flagged to be revisited toggle */}
            <div className="flex items-end">
              <div className="flex items-center space-x-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800 h-10">
                <Checkbox
                  id="flagged-to-revisit"
                  checked={flaggedToBeRevisited}
                  onCheckedChange={(checked) => setFlaggedToBeRevisited(checked as boolean)}
                  disabled={isSubmitting}
                />
                <Label 
                  htmlFor="flagged-to-revisit" 
                  className="flex items-center gap-2 cursor-pointer text-amber-700 dark:text-amber-400 text-sm whitespace-nowrap"
                >
                  <Flag className="h-4 w-4" />
                  Flag to revisit
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-idea-name">Initiative Name *</Label>
            <Input
              id="edit-idea-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter initiative name"
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-target-year">Target Year *</Label>
            <Select 
              value={targetYear.toString()} 
              onValueChange={(value) => setTargetYear(parseInt(value))} 
              disabled={isSubmitting}
            >
              <SelectTrigger id="edit-target-year">
                <SelectValue placeholder="Select target year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Year 1 (What quarterly tasks must we deliver)</SelectItem>
                <SelectItem value="2">Year 2 (What initiatives will we need)</SelectItem>
                <SelectItem value="3">Year 3 (What has to be true)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-idea-area">Business Area</Label>
            <Select value={area} onValueChange={setArea} disabled={isSubmitting}>
              <SelectTrigger id="edit-idea-area">
                <SelectValue placeholder="Select business area">
                  {area && PERSPECTIVE_COLORS[area] && (
                    <span className="flex items-center gap-2">
                      <span>{getAreaDisplayLabel(area, currencySymbol)}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${PERSPECTIVE_COLORS[area].bg} ${PERSPECTIVE_COLORS[area].text}`}>
                        {PERSPECTIVE_COLORS[area].code}
                      </span>
                    </span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {AREA_OPTIONS.map((option) => {
                  const displayLabel = getAreaDisplayLabel(option, currencySymbol);
                  return (
                    <SelectItem key={option} value={option}>
                      <span className="flex items-center gap-2">
                        <span>{displayLabel}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${PERSPECTIVE_COLORS[option].bg} ${PERSPECTIVE_COLORS[option].text}`}>
                          {PERSPECTIVE_COLORS[option].code}
                        </span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-idea-description">Description</Label>
            <Textarea
              id="edit-idea-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your initiative..."
              rows={4}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label>Affecting Metrics (Goals) - Priority Order</Label>
            {goalsLoading ? (
              <p className="text-sm text-muted-foreground">Loading goals...</p>
            ) : goals.length === 0 ? (
              <p className="text-sm text-muted-foreground">No goals available. Create goals first.</p>
            ) : (
              <PriorityGoalSelector
                goals={goals}
                selectedGoalIds={selectedGoals}
                onChange={handleGoalsChange}
                disabled={isSubmitting}
              />
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
